/**
 * Minimal DOM helpers (FR-39, FR-40, NFR-2).
 *
 * No framework. Three reasons, all of them requirements rather than taste:
 *
 *  - **FR-39 forbids reshuffling under the cursor.** A keyed reconciler that
 *    moves existing nodes instead of rebuilding lists is the whole ballgame
 *    for a board that repaints every 5 seconds while somebody's thumb is on it.
 *  - **FR-40 wants a genuinely minimal-DOM compact view** for bad venue wifi.
 *  - **NFR-6 says no build step a contributor cannot run**, and the smaller the
 *    dependency surface, the longer that stays true.
 *
 * `text()` is used for every value that came from outside this codebase.
 * Nothing in the app ever assigns `innerHTML` from source data (FR-56).
 */

export type Child = Node | string | number | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, unknown> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'dataset') {
      for (const [dk, dv] of Object.entries(value as Record<string, string>)) {
        node.dataset[dk] = dv;
      }
    } else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === 'style' && typeof value === 'object') {
      Object.assign(node.style, value);
    } else if (value === true) {
      node.setAttribute(key, '');
    } else {
      node.setAttribute(key, String(value));
    }
  }

  append(node, children);
  return node;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/** Always prefer this over innerHTML for anything derived from source data. */
export function text(value: unknown): Text {
  return document.createTextNode(value === null || value === undefined ? '' : String(value));
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/**
 * Keyed list reconciliation.
 *
 * Reuses the existing element for a key and moves it into place rather than
 * recreating it. This is what keeps scroll position, focus, and text selection
 * intact across a poll — FR-39's "must not reshuffle under the cursor" — and it
 * is also what makes a 300-row board repaint cheaply enough to hold NFR-2's
 * sub-500ms budget on a phone.
 *
 * `render(item, existing)` may mutate and return `existing` for an in-place
 * update, or return a fresh node.
 */
export function reconcile<T>(
  container: HTMLElement,
  items: T[],
  keyOf: (item: T) => string,
  render: (item: T, existing: HTMLElement | null) => HTMLElement,
): void {
  const existing = new Map<string, HTMLElement>();
  for (const child of Array.from(container.children)) {
    const key = (child as HTMLElement).dataset['key'];
    if (key) existing.set(key, child as HTMLElement);
  }

  let cursor: Element | null = container.firstElementChild;

  for (const item of items) {
    const key = keyOf(item);
    const prior = existing.get(key) ?? null;
    const node = render(item, prior);
    node.dataset['key'] = key;
    existing.delete(key);

    if (cursor === node) {
      cursor = cursor.nextElementSibling;
    } else {
      container.insertBefore(node, cursor);
    }
  }

  // Anything left over is genuinely gone from the list.
  for (const orphan of existing.values()) orphan.remove();
}

export function on<K extends keyof DocumentEventMap>(
  target: EventTarget,
  type: K,
  handler: (event: DocumentEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void {
  target.addEventListener(type, handler as EventListener, options);
  return () => target.removeEventListener(type, handler as EventListener, options);
}

export const fmt = {
  pct: (n: number): string => `${Math.round(n * 100)}%`,
  signed: (n: number, dp = 1): string => `${n >= 0 ? '+' : ''}${n.toFixed(dp)}`,
  num: (n: number, dp = 0): string => n.toFixed(dp),
  compactNum: (n: number): string => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)),
  ago: (ms: number): string => {
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.round(minutes / 60)}h ago`;
  },
};
